import React, { useState, useEffect, useRef } from "react";
import "./AtendimentoVirtual.css";
import api from "../Services/api";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination } from "swiper";

interface AtendimentoItem {
  titulo: string;
  link?: string;
  icone?: string;
  identificador?: string;
}

SwiperCore.use([Pagination]);

export async function getMenuAtendimentoVirtual() {
  try {
    const response = await api.get("ObterMenuAtendimentoVirtual", {
      params: {
        aplicacao: "Institucional",
        plano: 1,
        sitpart: "22/022",
        beneficio: "013",
      },
    });
    return response.data.results || response.data || [];
  } catch (error) {
    console.error("Erro ao buscar menu do SharePoint:", error);
    throw error;
  }
}

const MOBILE_BREAKPOINT = 768;

function useWindowWidth() {
  const [w, setW] = useState<number>(window.innerWidth);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return w;
}

// Hook para verificar se a fonte Material Icons está carregada
const useMaterialIconsLoaded = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Verificar se a fonte já está carregada
    if (document.fonts) {
      document.fonts.load('12px "Material Symbols Outlined"').then(() => {
        setLoaded(true);
      }).catch(() => {
        // Fallback: esperar um tempo
        setTimeout(() => setLoaded(true), 100);
      });
    } else {
      // Fallback para navegadores mais antigos
      const timer = setTimeout(() => setLoaded(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  return loaded;
};

const AtendimentoVirtual: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [items, setItems] = useState<AtendimentoItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [iconsVisible, setIconsVisible] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < MOBILE_BREAKPOINT;
  
  // CORREÇÃO: Usar useRef para o Swiper de forma correta
  const swiperElRef = useRef<HTMLDivElement>(null);
  const swiperInstanceRef = useRef<any>(null);
  
  const materialIconsLoaded = useMaterialIconsLoaded();

  const buildUrl = (link?: string): string => {
    if (!link || link === "#") return "#";
    if (/^https?:\/\//i.test(link)) return link;
    if (link.includes("participante.forluz.org.br")) return `https://${link}`;
    if (link.startsWith("/"))
      return `https://institucional.forluz.org.br${link}`;
    return `https://institucional.forluz.org.br/${link}`;
  };

  useEffect(() => {
    (async () => {
      const response = await api.get("/ObterPublicacoesHome");
      const itemRentabilidade = response.data.results.find((item: { URL: string | string[]; }) => { 
        return item.URL && item.URL && item.URL.indexOf("/Rentabilidades/") > 0 
      });

      try {
        setIsLoading(true);
        const apiData = await getMenuAtendimentoVirtual();
        if (apiData && apiData.itensMenuAtendimentoVirtual?.length > 0) {
          const processedItems = apiData.itensMenuAtendimentoVirtual.map((item: any) => {
            if (item.identificador === "rentabilidade" && itemRentabilidade) {
              // item.link = itemRentabilidade.URL;
            }
            return item;
          });
          setItems(processedItems);
        } else {
          setItems([]);
        }
      } catch {
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Mostrar ícones após a fonte carregar
  useEffect(() => {
    if (materialIconsLoaded && !isLoading) {
      const timer = setTimeout(() => {
        setIconsVisible(true);
        // Atualizar o Swiper após os ícones ficarem visíveis
        if (swiperInstanceRef.current) {
          setTimeout(() => {
            swiperInstanceRef.current.update();
          }, 50);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [materialIconsLoaded, isLoading]);

  // Função para inicializar o Swiper
  const handleSwiperInit = (swiper: any) => {
    swiperInstanceRef.current = swiper;
    
    if (iconsVisible) {
      setTimeout(() => {
        swiper.update();
        swiper.slideTo(0, 0);
      }, 100);
    }
  };

  // Função para atualizar o Swiper quando os ícones ficarem visíveis
  const updateSwiper = () => {
    if (swiperInstanceRef.current && iconsVisible) {
      swiperInstanceRef.current.update();
      swiperInstanceRef.current.slideTo(swiperInstanceRef.current.activeIndex, 0);
    }
  };

  // Chamar updateSwiper quando iconsVisible mudar
  useEffect(() => {
    if (iconsVisible) {
      // Tentar atualizar múltiplas vezes para garantir
      setTimeout(updateSwiper, 100);
      setTimeout(updateSwiper, 300);
      setTimeout(updateSwiper, 500);
    }
  }, [iconsVisible]);

  const renderItem = (item: AtendimentoItem, index: number) => {
    const finalUrl = buildUrl(item.link);
    const isExternal = finalUrl.includes("participante.forluz.org.br");
    const content = (
      <>
        <div className="icon-wrapper">
          {/* Placeholder enquanto a fonte não carrega */}
          {!iconsVisible && (
            <div 
              className="icon-fallback" 
              data-icon={item.icone}
              style={{ 
                width: '24px', 
                height: '24px', 
                background: '#e0e0e0',
                borderRadius: '4px'
              }}
            />
          )}
          <span 
            className="material-symbols-outlined"
            style={{ 
              opacity: iconsVisible ? 1 : 0,
              transition: 'opacity 0.2s ease-in',
              visibility: iconsVisible ? 'visible' : 'hidden'
            }}
          >
            {item.icone}
          </span>
        </div>
        <p style={{ 
          opacity: iconsVisible ? 1 : 0.6,
          transition: 'opacity 0.3s ease-in',
          fontWeight: iconsVisible ? 'normal' : '300'
        }}>
          {item.titulo}
        </p>
      </>
    );

    return (
      <div className="atendimento-item" key={`${item.identificador}-${index}`}>
        {finalUrl && finalUrl !== "#" ? (
          <a
            href={finalUrl}
            className="atendimento-link"
            target={isExternal ? "_blank" : "_self"}
            rel={isExternal ? "noopener noreferrer" : undefined}
            draggable={false}
          >
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    );
  };

  const getFallback = (): AtendimentoItem[] => [
    {
      titulo: "Contracheque",
      identificador: "contracheque",
      icone: "payments",
      link: "participante.forluz.org.br",
    },
    {
      titulo: "Extrato",
      identificador: "extrato_conta_ativo",
      icone: "receipt_long",
      link: "participante.forluz.org.br",
    },
    {
      titulo: "Rentabilidade",
      identificador: "rentabilidade",
      icone: "trending_up",
      link: "/docs/Publicacoes/Rentabilidades/",
    },
    {
      titulo: "Empréstimo",
      identificador: "simular_emprestimo",
      icone: "price_check",
      link: "participante.forluz.org.br",
    },
    {
      titulo: "Beneficiários",
      identificador: "incluir_excluir_beneficiario",
      icone: "groups",
      link: "participante.forluz.org.br",
    },
    {
      titulo: "Adesão",
      identificador: "adesao",
      icone: "person_add",
      link: "participante.forluz.org.br",
    },
    {
      titulo: "Central de Atendimento",
      identificador: "central_atendimento",
      icone: "support_agent",
      link: "/CentralDeAtendimento/central-atendimento",
    },
  ];

  if (isLoading) {
    return (
      <div className="atendimento-container loading">
        <div className="atendimento-content desktop-version">
          <div className="atendimento-title">
            <h2>Atendimento</h2>
            <h3>Virtual</h3>
          </div>
          <div className="atendimento-items">
            {getFallback().map((item, i) => (
              <div className="atendimento-item" key={`fallback-${i}`}>
                <div className="icon-wrapper">
                  <div className="icon-fallback" />
                </div>
                <p>{item.titulo}</p>
              </div>
            ))}
          </div>
        </div>
        <hr className="divider" />
      </div>
    );
  }

  return (
    <div className="atendimento-container">
      {/* Desktop (grid simples) */}
      <div className="atendimento-content desktop-version">
        <div className="atendimento-title">
          <h2>Atendimento</h2>
          <h3>Virtual</h3>
        </div>
        <div className="atendimento-items">
          {items.map((item, i) => renderItem(item, i))}
        </div>
      </div>

      {/* Mobile (Swiper) */}
      <div className="mobile-version">
        <div className="swiper-atendimento-wrapper">
          {/* CORREÇÃO: Removido o ref do Swiper e usando onSwiper */}
          <Swiper
            onSwiper={handleSwiperInit}
            onSlideChange={(sw) => setCurrentSlide(sw.activeIndex)}
            pagination={{
              clickable: true,
              el: ".atendimento-swiper-pagination",
            }}
            watchOverflow={true}
            slidesPerView={'auto'}
            spaceBetween={16}
            freeMode={false}
            centeredSlides={false}
            
            // Configurações CRÍTICAS para o Swiper
            observer={true}
            observeParents={true}
            observeSlideChildren={true}
            updateOnImagesReady={true}
            initialSlide={0}
            
            breakpoints={{
              769: {
                slidesPerView: 'auto',
                spaceBetween: 24,
              },
              600: {
                slidesPerView: 'auto',
                spaceBetween: 20,
              },
              480: {
                slidesPerView: 'auto',
                spaceBetween: 14,
              },
              320: {
                slidesPerView: 'auto',
                spaceBetween: 12,
              },
            }}
          >
            {items.map((item, i) => (
              <SwiperSlide
                key={`sw-${item.identificador}-${i}`}
                className={`atendimento-slide ${iconsVisible ? 'slide-visible' : ''}`}
              >
                {renderItem(item, i)}
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="atendimento-swiper-pagination" />
        </div>
      </div>

      <hr className="divider" />
    </div>
  );
};

export default AtendimentoVirtual;